import React, { useId } from 'react';
import { AlertCircle, Check, Eye, EyeOff, Search } from 'lucide-react';

/**
 * Champs de formulaire du socle (passation §4, maquette « Formulaires LIAmani »).
 * Les styles vivent dans index.css (classes .field-*) : ces composants posent la structure —
 * libellé, aide, message d'erreur avec icône, unité, icône de recherche — et les attributs
 * d'accessibilité (htmlFor, aria-invalid, aria-describedby). Tout suit les jetons de thème.
 */

interface FieldProps {
    label?: React.ReactNode;
    required?: boolean;
    /** Aide sous le champ (12 px, discrète). Pour un champ désactivé : dire pourquoi. */
    help?: React.ReactNode;
    /** Message d'erreur : bordure rouge + texte avec icône, jamais seul en rouge. */
    error?: React.ReactNode;
    /** Compteur à droite de l'aide (« 78 / 500 »). */
    counter?: React.ReactNode;
    disabled?: boolean;
    className?: string;
    children: (ids: { id: string; describedBy?: string; invalid: boolean }) => React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({ label, required, help, error, counter, disabled, className = '', children }) => {
    const id = useId();
    const helpId = help || error ? `${id}-help` : undefined;
    return (
        <div className={className}>
            {label && (
                <label htmlFor={id} className={`field-label ${disabled ? 'field-label--disabled' : ''}`}>
                    {label}{required && <span className="field-required"> *</span>}
                </label>
            )}
            {children({ id, describedBy: helpId, invalid: !!error })}
            {error ? (
                <span id={helpId} className="field-error" role="alert"><AlertCircle size={13} /> {error}</span>
            ) : (help || counter) ? (
                <span id={helpId} className="flex justify-between gap-3"><span className="field-help">{help}</span>{counter && <span className="field-help field-counter">{counter}</span>}</span>
            ) : null}
        </div>
    );
};

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };
export const Input = React.forwardRef<HTMLInputElement, InputProps>(({ className = '', invalid, ...rest }, ref) => (
    <input ref={ref} className={`field-input ${className}`} aria-invalid={invalid || undefined} {...rest} />
));
Input.displayName = 'Input';

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean };
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className = '', invalid, ...rest }, ref) => (
    <textarea ref={ref} className={`field-textarea ${className}`} aria-invalid={invalid || undefined} {...rest} />
));
Textarea.displayName = 'Textarea';

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean };
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({ className = '', invalid, children, ...rest }, ref) => (
    <select ref={ref} className={`field-select ${className}`} aria-invalid={invalid || undefined} {...rest}>{children}</select>
));
Select.displayName = 'Select';

/** Champ avec unité accolée à droite (€, min, m²). */
export const UnitInput: React.FC<InputProps & { unit: React.ReactNode }> = ({ unit, className = '', invalid, disabled, ...rest }) => (
    <div className={`field-box field-box--unit ${className}`} aria-invalid={invalid || undefined} aria-disabled={disabled || undefined}>
        <input disabled={disabled} {...rest} />
        <span className="field-unit">{unit}</span>
    </div>
);

/** Mot de passe avec bouton œil à droite : un clic affiche, un second clic masque. */
export const PasswordInput: React.FC<Omit<InputProps, 'type'>> = ({ className = '', invalid, disabled, ...rest }) => {
    const [visible, setVisible] = React.useState(false);
    return (
        <div className={`field-box pr-1.5 ${className}`} aria-invalid={invalid || undefined} aria-disabled={disabled || undefined}>
            <input type={visible ? 'text' : 'password'} disabled={disabled} aria-invalid={invalid || undefined} {...rest} />
            <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                disabled={disabled}
                aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                aria-pressed={visible}
                title={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                className="shrink-0 w-8 h-8 rounded-md inline-flex items-center justify-center text-content-muted hover:text-content hover:bg-surface-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--acc-ring)]"
            >
                {visible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
        </div>
    );
};

/** Champ de recherche : icône à gauche, compteur de résultats facultatif à droite. */
export const SearchInput: React.FC<InputProps & { results?: React.ReactNode }> = ({ results, className = '', disabled, ...rest }) => (
    <div className={`field-box ${className}`} aria-disabled={disabled || undefined}>
        <Search size={15} className="field-box-icon" />
        <input type="search" disabled={disabled} {...rest} />
        {results !== undefined && <span className="font-mono text-[10.5px] text-content-muted whitespace-nowrap">{results}</span>}
    </div>
);

interface CheckProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: React.ReactNode;
    /** Ligne d'explication sous le libellé. */
    sub?: React.ReactNode;
}
export const Checkbox: React.FC<CheckProps> = ({ label, sub, disabled, className = '', ...rest }) => (
    <label className={`field-check ${disabled ? 'field-check--disabled' : ''} ${className}`}>
        <input type="checkbox" disabled={disabled} {...rest} />
        <span className="field-check-box"><Check size={12} strokeWidth={3} /></span>
        <span>{label}{sub && <span className="field-check-sub">{sub}</span>}</span>
    </label>
);
export const Radio: React.FC<CheckProps> = ({ label, sub, disabled, className = '', ...rest }) => (
    <label className={`field-check field-check--radio ${disabled ? 'field-check--disabled' : ''} ${className}`}>
        <input type="radio" disabled={disabled} {...rest} />
        <span className="field-check-box" />
        <span>{label}{sub && <span className="field-check-sub">{sub}</span>}</span>
    </label>
);

/** Interrupteur 38 × 22 ; avec `label`, rendu en ligne « libellé … interrupteur ». */
export const Switch: React.FC<Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: React.ReactNode }> = ({ label, className = '', ...rest }) => {
    const control = (
        <span className={`field-switch ${className}`}>
            <input type="checkbox" role="switch" aria-checked={!!rest.checked} {...rest} />
            <span className="field-switch-knob" />
        </span>
    );
    if (!label) return control;
    return <label className="field-switch-row"><span className={rest.checked ? '' : 'text-content-muted'}>{label}</span>{control}</label>;
};

/** Contrôle segmenté : une seule valeur parmi deux ou trois. */
export function Segmented<T extends string>({ value, options, onChange, className = '', ariaLabel }: { value: T; options: { value: T; label: React.ReactNode }[]; onChange: (v: T) => void; className?: string; ariaLabel?: string }) {
    return (
        <div className={`segmented ${className}`} role="group" aria-label={ariaLabel}>
            {options.map((o) => (
                <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
            ))}
        </div>
    );
}
